/**
 * Official Government JSON Schema Exporter for E-Way Bill and E-Invoice
 * 
 * Conforms to:
 * - E-Way Bill: NIC Schema Version 1.0.04 (https://ewaybillgst.gov.in)
 * - E-Invoice: NIC Standard Form INV-01 Schema Version 1.1 (https://einvoice1.gst.gov.in)
 */

export interface GovCompanyDetails {
  gstin: string;
  legalName: string;
  tradeName: string;
  address1: string;
  address2?: string;
  location: string;
  pincode: string;
  stateCode: string;
  phone?: string;
  email?: string;
}

export interface GovItem {
  itemNo: number;
  productName: string;
  description?: string;
  hsnCode: string;
  quantity: number;
  unit: string;
  unitRate: number;
  taxableAmount: number;
  gstRate: number;
  cgstAmount: number;
  sgstAmount: number;
  igstAmount: number;
  cessAmount?: number;
  totalAmount: number;
}

export interface GovRecipientDetails {
  gstin?: string;
  tradeName: string;
  legalName?: string;
  address1: string;
  location: string;
  pincode: string;
  state: string;
  stateCode: string;
}

export interface EWayBillExportData {
  userGstin: string;
  supplyType: "O" | "I"; // Outward / Inward
  subSupplyType: number; // 1: Supply, 2: Import, 3: Export, etc.
  docType: "INV" | "BIL" | "BOE" | "CHL" | "OTH";
  docNo: string;
  docDate: string; // DD/MM/YYYY
  fromGstin: string;
  fromTrdName: string;
  fromAddr1: string;
  fromPlace: string;
  fromPincode: number;
  fromStateCode: number;
  toGstin: string;
  toTrdName: string;
  toAddr1: string;
  toPlace: string;
  toPincode: number;
  toStateCode: number;
  totalValue: number;
  cgstValue: number;
  sgstValue: number;
  igstValue: number;
  cessValue: number;
  totInvValue: number;
  transMode: number; // 1: Road, 2: Rail, 3: Air, 4: Ship
  transDistance: number;
  transporterName?: string;
  transporterId?: string;
  transDocNo?: string;
  transDocDate?: string;
  vehicleNo?: string;
  vehicleType?: "R" | "O"; // Regular / Over Dimensional
  itemList: {
    itemNo: number;
    productName: string;
    productDesc: string;
    hsnCode: number;
    quantity: number;
    qtyUnit: string;
    cgstRate: number;
    sgstRate: number;
    igstRate: number;
    cessRate: number;
    taxableAmount: number;
  }[];
}

// State code helper
export const STATE_CODE_MAP: Record<string, number> = {
  "Jammu & Kashmir": 1, "Himachal Pradesh": 2, "Punjab": 3, "Chandigarh": 4,
  "Uttarakhand": 5, "Haryana": 6, "Delhi": 7, "Rajasthan": 8, "Uttar Pradesh": 9,
  "Bihar": 10, "Sikkim": 11, "Arunachal Pradesh": 12, "Nagaland": 13, "Manipur": 14,
  "Mizoram": 15, "Tripura": 16, "Meghalaya": 17, "Assam": 18, "West Bengal": 19,
  "Jharkhand": 20, "Odisha": 21, "Chhattisgarh": 22, "Madhya Pradesh": 23, "Gujarat": 24,
  "Maharashtra": 27, "Andhra Pradesh": 28, "Karnataka": 29, "Goa": 30, "Kerala": 32,
  "Tamil Nadu": 33, "Puducherry": 34, "Telangana": 36, "Ladakh": 38
};

export function getStateCode(stateName?: string, gstin?: string): number {
  if (gstin && gstin.length >= 2 && !isNaN(Number(gstin.slice(0, 2)))) {
    return Number(gstin.slice(0, 2));
  }
  if (stateName && STATE_CODE_MAP[stateName]) {
    return STATE_CODE_MAP[stateName];
  }
  return 3; // Default Punjab
}

export function formatDateGovt(dateStr: string | Date): string {
  const d = new Date(dateStr);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

/**
 * Generate NIC compliant E-Way Bill JSON for bulk upload
 */
export function buildEWayBillGovtJson(bills: EWayBillExportData[]) {
  return {
    version: "1.0.04",
    billLists: bills
  };
}

/**
 * Generate NIC Standard Form INV-01 E-Invoice JSON
 */
export function buildEInvoiceGovtJson(
  voucher: any,
  company: GovCompanyDetails,
  recipient: GovRecipientDetails,
  items: GovItem[]
) {
  const docDate = formatDateGovt(voucher.date || new Date());
  const isInterState = company.stateCode !== recipient.stateCode;

  const assVal = items.reduce((s, it) => s + it.taxableAmount, 0);
  const cgstVal = isInterState ? 0 : items.reduce((s, it) => s + it.cgstAmount, 0);
  const sgstVal = isInterState ? 0 : items.reduce((s, it) => s + it.sgstAmount, 0);
  const igstVal = isInterState ? items.reduce((s, it) => s + it.igstAmount, 0) : 0;
  const totInvVal = Math.round((assVal + cgstVal + sgstVal + igstVal) * 100) / 100;

  return {
    Version: "1.1",
    TranDtls: {
      TaxSch: "GST",
      SupTyp: recipient.gstin && recipient.gstin.length === 15 ? "B2B" : "B2C",
      RegRev: "N",
      EcmGstin: null,
      IgstOnIntra: "N"
    },
    DocDtls: {
      Typ: "INV",
      No: voucher.invoiceNumber || voucher.voucherNumber,
      Dt: docDate
    },
    SellerDtls: {
      Gstin: company.gstin,
      LglNm: company.legalName,
      TrdNm: company.tradeName,
      Addr1: company.address1,
      Addr2: company.address2 || "",
      Loc: company.location,
      Pin: Number(company.pincode) || 147301,
      Stcd: company.stateCode,
      Ph: company.phone || null,
      Em: company.email || null
    },
    BuyerDtls: {
      Gstin: recipient.gstin || "URP",
      LglNm: recipient.legalName || recipient.tradeName,
      TrdNm: recipient.tradeName,
      Pos: recipient.stateCode,
      Addr1: recipient.address1 || "Punjab",
      Loc: recipient.location || "Mandi Gobindgarh",
      Pin: Number(recipient.pincode) || 147301,
      Stcd: recipient.stateCode
    },
    ItemList: items.map((it, idx) => ({
      SlNo: String(idx + 1),
      PrdDesc: it.productName,
      IsServc: it.hsnCode.startsWith("99") ? "Y" : "N",
      HsnCd: it.hsnCode,
      Qty: it.quantity,
      Unit: it.unit || "PCS",
      UnitPrice: it.unitRate,
      TotAmt: it.taxableAmount,
      Discount: 0,
      PreTaxVal: 0,
      AssAmt: it.taxableAmount,
      GstRt: it.gstRate,
      IgstAmt: isInterState ? it.igstAmount : 0,
      CgstAmt: isInterState ? 0 : it.cgstAmount,
      SgstAmt: isInterState ? 0 : it.sgstAmount,
      CesRt: 0,
      CesAmt: 0,
      CesNonAdvlAmt: 0,
      StateCesRt: 0,
      StateCesAmt: 0,
      StateCesNonAdvlAmt: 0,
      OthChrg: 0,
      TotItemVal: it.totalAmount
    })),
    ValDtls: {
      AssVal: assVal,
      CgstVal: cgstVal,
      SgstVal: sgstVal,
      IgstVal: igstVal,
      CesVal: 0,
      StCesVal: 0,
      Discount: 0,
      OthChrg: 0,
      RndOffAmt: 0,
      TotInvVal: totInvVal
    }
  };
}

/**
 * Trigger download of JSON file in browser
 */
export function downloadJsonFile(filename: string, data: object) {
  const jsonStr = JSON.stringify(data, null, 2);
  const blob = new Blob([jsonStr], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
